<template>
  <div class="stats">
    <div class="stats__separator stats__separator--horizontal" />
    <div
      class="stats__container"
      :class="{ 'stats__container--three-columns': hasTrackableContent }"
    >
      <span class="stats__duration">
        <div class="stats__duration-mobile stats__duration-mobile-label">
          Duration
        </div>
        {{ courseDuration }}
      </span>
      <span class="stats__separator" />

      <template v-if="hasTrackableContent">
        <span class="stats__cpe-credits-base">
          <span class="stats__cpe-credits-base-mobile">
            <div class="stats__cpe-credits-base-mobile-label">CPE</div>
            <div>{{ course.cpe_credits }} credits</div>
          </span>
          <span class="stats__cpe-credits-base-desktop">
            {{ course.cpe_credits }} CPE
            {{ $pluralizeWord('Credit', course.cpe_credits) }}
            <InfoIcon
              v-if="isAuthenticated"
              v-tooltip="{
                content: cpeTooltipText,
                placement: 'right',
                classes: ['tooltip--text-center'],
              }"
              size="14"
            />
          </span>
        </span>
        <span class="stats__separator" />
      </template>

      <template v-if="hasSkillCheckLab">
        <div class="stats__cpe-credits-check">
          <div class="stats__cpe-credits-check-labs">
            <SkillCheckIcon /> {{ skillCheckLabel }}, {{ skillCheckFlagsLabel }}
          </div>
          <div class="stats__cpe-credits-check-bonus">
            +{{ skillCheckCredits }} Bonus CPE
            {{ $pluralizeWord('Credit', skillCheckCredits) }}
          </div>
        </div>
        <span class="stats__separator stats__separator--hidden-on-mobile" />
      </template>
      <span class="stats__difficulty">
        <div class="stats__difficulty-mobile stats__difficulty-mobile-label">
          Difficulty
        </div>
        <Difficulty
          :difficulty="courseDifficulty"
          alwaysVisible
          hideIconOnMobile
          colored
        />
      </span>
      <span class="stats__separator stats__separator--hidden-on-mobile" />
      <span v-if="hasAccess" class="stats__files">
        <button
          v-if="fileUUIDs.length"
          v-tooltip="{
            content: `Download files related<br/>to this course`,
            classes: ['tooltip--text-center'],
          }"
          class="stats__files-btn-download btn btn--small btn--default-icon-color btn--full-mobile"
          :class="{ 'btn--secondary': !isDesktop, 'btn--tertiary': isDesktop }"
          @click="download"
        >
          <DownloadIcon />
          Download files
        </button>
        <template v-else>
          No course files
        </template>
      </span>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import Difficulty from '@/components/properties/Difficulty.vue'
import { InfoIcon, DownloadIcon } from 'vue-feather-icons'
import SkillCheckIcon from '@/assets/icons/skill_check_icon.svg'
import { hasBaseAccess } from '@/helpers'
import CONSTANTS from '@/constants'

export default {
  name: 'Stats',
  components: {
    Difficulty,
    InfoIcon,
    DownloadIcon,
    SkillCheckIcon,
  },
  inject: ['getWindowWidth'],
  props: {
    course: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState({
      isAuthenticated: state => state.auth.isAuthenticated,
    }),
    ...mapGetters({
      currentLabs: 'content/currentLabs',
    }),
    isDesktop() {
      return this.getWindowWidth() >= 1024
    },
    hasAccess() {
      return hasBaseAccess(this.course)
    },
    courseId() {
      return this.course.id
    },
    courseDifficulty() {
      return this.course.difficulty
    },
    courseDuration() {
      const { duration_in_seconds: duration } = this.course
      return this.$duration.durationFromSecs(duration)
    },
    cpeTooltipText() {
      if (!this.course.cpe_credits) {
        return 'This course does not provide CPE credits'
      }
      return CONSTANTS.CPE.CPE_PROGRAM_CONTENT_TOOLTIP_MSG
    },
    hasTrackableContent() {
      return this.course.has_trackable_content
    },
    hasSkillCheckLab() {
      return !!this.skillCheckLabs.length
    },
    skillCheckLabs() {
      return this.currentLabs.filter(lab => lab.is_skill_check)
    },
    skillCheckFlags() {
      const { skillCheckCredits } = this
      return skillCheckCredits ? skillCheckCredits / 2 : 0
    },
    skillCheckCredits() {
      return this.skillCheckLabs.reduce((acc, lab) => acc + lab.cpe_credits, 0)
    },
    fileUUIDs() {
      return this.course.files_uuids
    },
    skillCheckLabel() {
      const skillCheckLabsLength = this.skillCheckLabs?.length
      return `${skillCheckLabsLength} ${this.$pluralizeWord(
        'Lab',
        skillCheckLabsLength
      )}`
    },
    skillCheckFlagsLabel() {
      return `${this.skillCheckFlags} ${this.$pluralizeWord(
        'Flag',
        this.skillCheckFlags
      )}`
    },
  },
  methods: {
    ...mapActions({
      getFileDownloadLink: 'files/getFileDownloadLink',
    }),
    async download() {
      for (const fileUUID of this.fileUUIDs) {
        const response = await this.getFileDownloadLink(fileUUID)
        const tmp = document.createElement('a')
        tmp.href = response.download_url
        tmp.download = response.filename
        tmp.click()
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.stats {
  &__container {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    grid-template-rows: 40px auto auto;
    grid-row-gap: 15px;
    align-items: center;

    color: white;
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 450;
    line-height: 1.25rem;
    text-align: center;

    @include desktop {
      display: flex;
      align-items: center;
      margin: 0 40px;
      height: 45px;
      flex: none;
    }

    &--three-columns {
      grid-template-columns: 1fr auto 1fr auto 1fr;
    }
  }
  &__duration {
    grid-row: 1;
  }
  &__difficulty {
    justify-self: center;
    grid-row: 1;
    grid-column: 3;
  }
  &__cpe-credits-base {
    grid-row: 1;
    grid-column: 5;

    &-desktop {
      display: none;
      @include desktop {
        display: flex;
        align-items: center;
        svg {
          margin-left: 5px;
        }
      }
    }
  }
  &__duration,
  &__difficulty,
  &__cpe-credits-base {
    &-mobile {
      @include desktop {
        display: none;
      }
      &-label {
        color: var(--light-blue-gray);

        font-size: 0.75rem;
        font-style: normal;
        font-weight: 400;
        line-height: 0.875rem;
      }
    }
  }
  &__cpe-credits-check {
    grid-row: 2;
    grid-column: 1 / -1;

    &-labs {
      display: flex;
      justify-content: center;
      align-items: center;

      svg {
        margin-right: 5px;
      }
    }
    &-bonus {
      color: var(--positive-light);
    }
  }
  &__files {
    grid-column: 1 / -1;
  }

  &__separator {
    margin: 0;

    height: 100%;
    width: 1px;
    background: var(--glass-light-gray);

    @include desktop {
      margin: 0 25px;
    }

    &--horizontal {
      margin: 20px 0;
      width: 100%;
      height: 1px;
      @include desktop {
        display: none;
      }
    }
    &--hidden-on-mobile {
      display: none;
      @include desktop {
        display: initial;
      }
    }
  }
}
</style>
